import { TbPlus, TbTrash, TbX } from "solid-icons/tb";
import { type Accessor, type Component, createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { parseEmailInput } from "./parseEmailInput";

interface EmailTagsInputProps {
  /** Current list of emails (controlled) */
  emails: Accessor<string[]>;
  /** Callback when emails change */
  onEmailsChange: (emails: string[]) => void;
  /** Maximum number of emails allowed */
  maxEmails?: number;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Label text above the input */
  label?: string;
  /** Error message to display */
  error?: Accessor<string | null>;
  /** Callback when error changes (for validation errors) */
  onError?: (error: string | null) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** When provided, renders a "Remove All" button next to the label whenever
   *  there is at least one email. */
  onRemoveAll?: () => void;
  /** Translated label for the Remove All button. Required if onRemoveAll is set. */
  removeAllLabel?: string;
  /** When provided, renders an "Add All" button next to the label whenever
   *  there is at least one suggestion that hasn't already been added. */
  onAddAll?: () => void;
  /** Translated label for the Add All button. Required if onAddAll is set. */
  addAllLabel?: string;
  /** When provided, enables an autocomplete dropdown anchored to the input.
   *  The dropdown filters this list (case-insensitive substring match on the
   *  full email) and excludes already-added emails. Pass undefined or omit to
   *  disable autocomplete. */
  suggestions?: Accessor<string[]>;
  /** Source for the "Add All" button, when it should differ from the dropdown
   *  suggestions (e.g. excluding historical recipients from bulk-add). Defaults
   *  to `suggestions` when omitted. */
  addAllSuggestions?: Accessor<string[]>;
}

const DEFAULT_MAX_EMAILS = 10;

export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const EmailTagsInput: Component<EmailTagsInputProps> = (props) => {
  const [inputValue, setInputValue] = createSignal("");
  const [highlightedIndex, setHighlightedIndex] = createSignal(0);
  const [suggestionsVisible, setSuggestionsVisible] = createSignal(false);
  const maxEmails = () => props.maxEmails ?? DEFAULT_MAX_EMAILS;

  const filteredSuggestions = createMemo<string[]>(() => {
    const all = props.suggestions?.() ?? [];
    if (all.length === 0) return [];
    const query = inputValue().trim().toLowerCase();
    if (query.length === 0) return [];
    const taken = new Set(props.emails().map((e) => e.toLowerCase()));
    return all.filter((s) => {
      const lower = s.toLowerCase();
      if (taken.has(lower)) return false;
      const atIdx = lower.indexOf("@");
      const localPart = atIdx === -1 ? lower : lower.slice(0, atIdx);
      return localPart.includes(query);
    });
  });

  const dropdownOpen = () => suggestionsVisible() && filteredSuggestions().length > 0;

  const addAllSuggestions = () => props.addAllSuggestions?.() ?? props.suggestions?.() ?? [];

  // Tracks whether the suggestion source has anything left to bulk-add. Used to
  // toggle the optional "Add All" header button.
  const hasAddableSuggestions = createMemo<boolean>(() => {
    const all = addAllSuggestions();
    if (all.length === 0) return false;
    const taken = new Set(props.emails().map((e) => e.toLowerCase()));
    return all.some((s) => !taken.has(s.toLowerCase()));
  });

  // Reset the highlight to the first item whenever the filtered list changes
  // so Enter/Tab pick a sensible default.
  createEffect(() => {
    filteredSuggestions();
    setHighlightedIndex(0);
  });

  const addEmail = (input: string) => {
    // Extracts plain emails plus RFC 5322 mailbox addresses (e.g. pasted
    // contact cards from Gmail/Outlook/Slack). See parseEmailInput for details.
    const parts = parseEmailInput(input);
    const currentEmails = props.emails();
    const newEmails = [...currentEmails];

    for (const part of parts) {
      const trimmed = part.trim().toLowerCase();
      if (!trimmed) continue;

      if (newEmails.length >= maxEmails()) {
        props.onError?.(`Maximum ${maxEmails()} emails allowed`);
        break;
      }

      if (!isValidEmail(trimmed)) {
        props.onError?.("Invalid email address");
        continue;
      }

      if (newEmails.includes(trimmed)) {
        continue;
      }

      newEmails.push(trimmed);
      props.onError?.(null);
    }

    if (newEmails.length !== currentEmails.length) {
      props.onEmailsChange(newEmails);
    }
    setInputValue("");
  };

  const removeEmail = (email: string) => {
    props.onEmailsChange(props.emails().filter((e) => e !== email));
  };

  const acceptHighlightedSuggestion = () => {
    const list = filteredSuggestions();
    if (list.length === 0) return false;
    const idx = highlightedIndex();
    const choice = list[Math.max(0, Math.min(idx, list.length - 1))];
    if (!choice) return false;
    addEmail(choice);
    setSuggestionsVisible(false);
    return true;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (dropdownOpen()) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const len = filteredSuggestions().length;
        setHighlightedIndex((i) => (i + 1) % len);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const len = filteredSuggestions().length;
        setHighlightedIndex((i) => (i - 1 + len) % len);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        acceptHighlightedSuggestion();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSuggestionsVisible(false);
        return;
      }
    }

    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail(inputValue());
      setSuggestionsVisible(false);
    } else if (e.key === "Backspace" && inputValue() === "" && props.emails().length > 0) {
      const currentEmails = props.emails();
      const lastEmail = currentEmails[currentEmails.length - 1];
      if (lastEmail) {
        removeEmail(lastEmail);
      }
    }
  };

  const handleBlur = () => {
    setSuggestionsVisible(false);
    if (inputValue().trim()) {
      addEmail(inputValue());
    }
  };

  // A pasted list of addresses becomes tags at once rather than sitting in
  // the field until something commits it. A single address pastes as text,
  // so the rest of one typed by hand can still be pasted in.
  const handlePaste = (e: ClipboardEvent) => {
    const pasted = e.clipboardData?.getData("text/plain") ?? "";
    if (parseEmailInput(pasted).length < 2 && !/<[^<>]+>/.test(pasted)) return;
    e.preventDefault();
    addEmail(pasted);
    setSuggestionsVisible(false);
  };

  let inputEl!: HTMLInputElement;
  const addTyped = () => {
    if (inputValue().trim()) addEmail(inputValue());
    inputEl.focus();
  };

  return (
    <div class="space-y-2">
      <Show
        when={
          props.label || (props.onAddAll && hasAddableSuggestions()) || (props.onRemoveAll && props.emails().length > 0)
        }
      >
        <div class="flex items-center justify-between">
          <Show when={props.label} fallback={<span />}>
            <label class="block text-sm font-medium text-primary-text">{props.label}</label>
          </Show>
          <div class="flex items-center gap-2">
            <Show when={props.onAddAll && hasAddableSuggestions()}>
              <button
                type="button"
                onClick={() => props.onAddAll?.()}
                disabled={props.disabled}
                class="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-primary-border bg-tile px-2 py-1 text-xs font-medium text-secondary-text transition-colors select-none hover:bg-panel-hover hover:text-primary-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TbPlus class="size-3.5 shrink-0" />
                {props.addAllLabel}
              </button>
            </Show>
            <Show when={props.onRemoveAll && props.emails().length > 0}>
              <button
                type="button"
                onClick={() => props.onRemoveAll?.()}
                disabled={props.disabled}
                class="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-primary-border bg-tile px-2 py-1 text-xs font-medium text-secondary-text transition-colors select-none hover:bg-panel-hover hover:text-primary-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <TbTrash class="size-3.5 shrink-0" />
                {props.removeAllLabel}
              </button>
            </Show>
          </div>
        </div>
      </Show>

      <div
        class={twMerge(
          "relative flex min-h-10.5 flex-wrap gap-2 rounded-lg border border-primary-border bg-panel p-2",
          props.disabled && "cursor-not-allowed opacity-50"
        )}
      >
        <For each={props.emails()}>
          {(email) => (
            <span class="inline-flex items-center gap-1 rounded-md bg-brand/10 px-2 py-1 text-sm text-primary-text">
              {email}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                disabled={props.disabled}
                class="text-secondary-text hover:text-primary-text disabled:cursor-not-allowed"
              >
                <TbX class="size-3" />
              </button>
            </span>
          )}
        </For>
        <input
          ref={inputEl}
          type="email"
          value={inputValue()}
          onInput={(e) => {
            setInputValue(e.currentTarget.value);
            setSuggestionsVisible(true);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={props.emails().length === 0 ? props.placeholder : ""}
          disabled={props.disabled || props.emails().length >= maxEmails()}
          class="min-w-30 flex-1 bg-transparent text-sm text-primary-text outline-none placeholder:text-secondary-text disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="button"
          aria-label="Add another email"
          title="Add another email"
          // mousedown would blur the input and commit the typed address a
          // second time; swallowing it keeps the field focused, and the click
          // (also what the keyboard sends) does the adding.
          onMouseDown={(e) => e.preventDefault()}
          onClick={addTyped}
          disabled={props.disabled || props.emails().length >= maxEmails()}
          class="grid size-6 shrink-0 place-items-center self-center rounded-md text-secondary-text hover:bg-panel-hover hover:text-primary-text disabled:cursor-not-allowed disabled:opacity-50"
        >
          <TbPlus class="size-4" />
        </button>
        <Show when={dropdownOpen()}>
          <div class="absolute inset-x-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-primary-border bg-panel shadow-xl">
            <For each={filteredSuggestions()}>
              {(suggestion, index) => (
                <button
                  type="button"
                  // mousedown fires before the input's blur, so the click registers
                  // before the dropdown closes.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addEmail(suggestion);
                    setSuggestionsVisible(false);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index())}
                  class={twMerge(
                    "block w-full cursor-pointer px-3 py-2 text-left text-sm text-primary-text",
                    index() === highlightedIndex() && "bg-brand/10"
                  )}
                >
                  {suggestion}
                </button>
              )}
            </For>
          </div>
        </Show>
      </div>

      <Show when={props.error?.()}>
        <p class="text-sm text-red-600 dark:text-red-400">{props.error?.()}</p>
      </Show>
    </div>
  );
};
